import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { makeMatcher } from '../../services/search';
import SearchField from '../SearchField';

const emptyForm = { email: '', password: '', firstName: '', lastName: '', role: 'user', isActive: true };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [apps, setApps] = useState([]);
  const [groups, setGroups] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [showApiKey, setShowApiKey] = useState({});
  const [accessUser, setAccessUser] = useState(null);
  const [search, setSearch] = useState('');

  // Accolades obligatoires : si load retournait la Promise, useEffect(load, [])
  // la stockerait comme fonction de nettoyage -> au démontage (navigation vers
  // une autre page) React l'appelle -> "destroy is not a function" -> page blanche.
  const load = () => {
    Promise.all([api.get('/users'), api.get('/applications/admin/all'), api.get('/groups')])
      .then(([u, a, g]) => { setUsers(u.data.data || []); setApps(a.data.data || []); setGroups(g.data.data || []); })
      .catch(e => setMsg(`Erreur : ${e.response?.data?.message || e.message}`))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openCreate = () => { setForm(emptyForm); setEditUser(null); setShowModal(true); };
  const openEdit = (u) => { setForm({ ...u, password: '' }); setEditUser(u); setShowModal(true); };

  // Filtrage local : la liste tient en mémoire, inutile de repasser par l'API.
  const correspond = makeMatcher(search);
  const usersFiltres = users.filter(u =>
    correspond(u.firstName, u.lastName, u.email, u.role, ...(u.groups || []).map(g => g.name)));

  const hasAccess = (user, appId) => (user.applications || []).some(a => a.id === appId);
  const inGroup = (user, groupId) => (user.groups || []).some(g => g.id === groupId);

  // Les outils hérités d'un groupe n'apparaissent pas dans user.applications
  // (qui ne porte que les accès individuels) : on les calcule ici pour les
  // afficher comme déjà accordés et non décochables.
  const groupAppIds = (user) => {
    const ids = new Set();
    for (const ug of user.groups || []) {
      const full = groups.find(g => g.id === ug.id);
      for (const a of full?.applications || []) ids.add(a.id);
    }
    return ids;
  };

  // Les bascules ci-dessous appliquent le changement à l'état local avant
  // l'aller-retour serveur : sans ça la case ne se coche qu'au retour de la
  // requête, soit environ une seconde de latence perçue à chaque clic.
  // En cas d'échec on restaure la valeur précédente et on affiche l'erreur.
  const patchUser = (userId, patch) => {
    setUsers(us => us.map(u => (u.id === userId ? { ...u, ...patch } : u)));
    setAccessUser(u => (u && u.id === userId ? { ...u, ...patch } : u));
  };

  const toggleAccess = async (user, appId) => {
    const previous = user.applications || [];
    const granted = hasAccess(user, appId);
    const next = granted
      ? previous.filter(a => a.id !== appId)
      : [...previous, apps.find(a => a.id === appId)].filter(Boolean);
    patchUser(user.id, { applications: next });
    try {
      if (granted) await api.delete(`/applications/admin/${appId}/revoke`, { data: { userId: user.id } });
      else await api.post(`/applications/admin/${appId}/grant`, { userId: user.id });
    } catch (e) {
      patchUser(user.id, { applications: previous });
      setMsg(`Erreur : ${e.response?.data?.message || e.message}`);
    }
  };

  const toggleGroup = async (user, groupId) => {
    const previous = user.groups || [];
    const next = inGroup(user, groupId)
      ? previous.filter(g => g.id !== groupId)
      : [...previous, groups.find(g => g.id === groupId)].filter(Boolean);
    patchUser(user.id, { groups: next });
    try {
      await api.put(`/groups/users/${user.id}`, { groupIds: next.map(g => g.id) });
    } catch (e) {
      patchUser(user.id, { groups: previous });
      setMsg(`Erreur : ${e.response?.data?.message || e.message}`);
    }
  };

  const handleSave = async () => {
    try {
      const payload = { ...form };
      if (editUser && !form.password) delete payload.password;
      if (editUser) await api.put(`/users/${editUser.id}`, payload);
      else await api.post('/users', payload);
      setShowModal(false);
      setMsg('Sauvegardé !');
      load();
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setMsg(`Erreur : ${e.response?.data?.message || e.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    await api.delete(`/users/${id}`);
    load();
  };

  const handleRegenKey = async (id) => {
    const r = await api.post(`/users/${id}/regenerate-key`);
    setShowApiKey({ ...showApiKey, [id]: r.data.data.apiKey });
    load();
  };

  return (
    <div>
      <div style={styles.header}>
        <h2 style={styles.title}>Utilisateurs</h2>
        <SearchField
          className="admin-search"
          value={search}
          onChange={setSearch}
          placeholder="Rechercher un utilisateur..."
        />
        <button onClick={openCreate} style={styles.addBtn}>+ Nouvel utilisateur</button>
      </div>
      {msg && <div style={{ ...styles.msg, background: msg.startsWith('Erreur') ? '#fff0ed' : '#e8f8e8', color: msg.startsWith('Erreur') ? '#E46138' : '#2d6a2d' }}>{msg}</div>}

      {loading ? <p>Chargement...</p> : (
        <div style={styles.table}>
          <div style={styles.tableHeader}>
            <span>Utilisateur</span><span>Rôle</span><span>Groupes</span><span>Statut</span><span>Actions</span>
          </div>
          {usersFiltres.length === 0 ? (
            <div style={styles.emptyRow}>
              {search.trim()
                ? `Aucun utilisateur ne correspond à « ${search.trim()} ».`
                : 'Aucun utilisateur enregistré pour le moment.'}
            </div>
          ) : usersFiltres.map(u => (
            <div key={u.id} style={styles.tableRow}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{u.firstName} {u.lastName}</div>
                <div style={{ fontSize: 12, color: '#7F7F7F' }}>{u.email}</div>
              </div>
              <span style={{ ...styles.badge, background: u.role === 'admin' ? '#fff0ed' : '#EEF3FF', color: u.role === 'admin' ? '#E46138' : '#0F56E3' }}>
                {u.role === 'admin' ? '👑 Admin' : '👤 User'}
              </span>
              <div style={styles.badgeList}>
                {showApiKey[u.id]
                  ? <code style={styles.apiKey}>{showApiKey[u.id]}</code>
                  : (u.groups || []).length === 0
                    ? <span style={{ fontSize: 12, color: '#7F7F7F' }}>—</span>
                    : u.groups.map(g => <span key={g.id} style={{ ...styles.badge, background: '#EEF3FF', color: '#0F56E3' }}>{g.name}</span>)}
              </div>
              <span style={{ ...styles.badge, background: u.isActive ? '#e8f8e8' : '#fafafa', color: u.isActive ? '#2d6a2d' : '#7F7F7F' }}>
                {u.isActive ? '✓ Actif' : '✗ Inactif'}
              </span>
              <div style={styles.actions}>
                <button onClick={() => setAccessUser(u)} style={styles.accessBtn} title="Groupes et accès">👥</button>
                <button onClick={() => handleRegenKey(u.id)} style={styles.keyBtn} title="Régénérer la clé API">🔑</button>
                <button onClick={() => openEdit(u)} style={styles.editBtn} title="Modifier">✏</button>
                {u.role !== 'admin' && <button onClick={() => handleDelete(u.id)} style={styles.deleteBtn} title="Supprimer">✕</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3>{editUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}</h3>
              <button onClick={() => setShowModal(false)} style={styles.closeBtn}>✕</button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.row}>
                <div style={styles.field}>
                  <label style={styles.label}>Prénom</label>
                  <input value={form.firstName || ''} onChange={e => setForm({...form, firstName: e.target.value})} style={styles.input} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Nom</label>
                  <input value={form.lastName || ''} onChange={e => setForm({...form, lastName: e.target.value})} style={styles.input} />
                </div>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Email *</label>
                <input type="email" value={form.email || ''} onChange={e => setForm({...form, email: e.target.value})} style={styles.input} required />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>{editUser ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe *'}</label>
                <input
                  type="password"
                  value={form.password || ''}
                  onChange={e => setForm({...form, password: e.target.value})}
                  style={styles.input}
                  minLength={12}
                  autoComplete="new-password"
                />
                <span style={styles.hint}>12 caractères minimum — le serveur refuse en deçà.</span>
              </div>
              <div style={styles.row}>
                <div style={styles.field}>
                  <label style={styles.label}>Rôle</label>
                  <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} style={styles.input}>
                    <option value="user">Utilisateur</option>
                    <option value="admin">Administrateur</option>
                  </select>
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.isActive} onChange={e => setForm({...form, isActive: e.target.checked})} />
                Compte actif
              </label>
            </div>
            <div style={styles.modalFooter}>
              <button onClick={() => setShowModal(false)} style={styles.cancelBtn}>Annuler</button>
              <button onClick={handleSave} style={styles.saveBtn}>Sauvegarder</button>
            </div>
          </div>
        </div>
      )}

      {accessUser && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3>Groupes et accès — {accessUser.firstName} {accessUser.lastName}</h3>
              <button onClick={() => setAccessUser(null)} style={styles.closeBtn}>✕</button>
            </div>
            <div style={styles.modalBody}>
              <div>
                <label style={styles.sectionLabel}>Groupes</label>
                <p style={styles.hint}>
                  L'utilisateur accède automatiquement à tous les outils de ses groupes.
                </p>
                <div style={styles.checkList}>
                  {groups.map(g => (
                    <label key={g.id} style={styles.accessRow}>
                      <input
                        type="checkbox"
                        style={styles.checkbox}
                        checked={inGroup(accessUser, g.id)}
                        onChange={() => toggleGroup(accessUser, g.id)}
                      />
                      <span style={styles.rowName}>{g.name}</span>
                      <span style={styles.rowMeta}>{g.applications?.length || 0} outil(s)</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label style={styles.sectionLabel}>Accès individuels</label>
                <p style={styles.hint}>
                  {accessUser.role === 'admin'
                    ? 'Cet utilisateur est administrateur : il accède déjà à tous les outils, quels que soient ses groupes.'
                    : 'À n\'utiliser que pour les exceptions, en plus des groupes.'}
                </p>
                <div style={styles.checkList}>
                  {apps.length === 0 ? (
                    <p style={{ fontSize: 13, color: '#7F7F7F' }}>Aucune application disponible.</p>
                  ) : apps.map(a => {
                    const inherited = groupAppIds(accessUser).has(a.id);
                    return (
                      <label key={a.id} style={{ ...styles.accessRow, opacity: inherited ? 0.65 : 1 }}>
                        <input
                          type="checkbox"
                          style={styles.checkbox}
                          checked={inherited || hasAccess(accessUser, a.id)}
                          disabled={inherited}
                          onChange={() => toggleAccess(accessUser, a.id)}
                        />
                        <span style={styles.rowName}>{a.name}</span>
                        {inherited && <span style={{ ...styles.rowMeta, color: '#019EF7' }}>via groupe</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button onClick={() => setAccessUser(null)} style={styles.saveBtn}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  header: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 700, color: '#062259' },
  addBtn: { marginLeft: 'auto', flexShrink: 0, background: '#062259', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  emptyRow: { padding: '28px 20px', textAlign: 'center', fontSize: 14, color: '#5f6673' },
  msg: { padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14 },
  table: { background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  tableHeader: { display: 'grid', gridTemplateColumns: '2fr 1fr 1.5fr 1fr 116px', gap: 16, padding: '12px 20px', background: '#f4f6f9', fontSize: 12, fontWeight: 600, color: '#7F7F7F', textTransform: 'uppercase' },
  tableRow: { display: 'grid', gridTemplateColumns: '2fr 1fr 1.5fr 1fr 116px', gap: 16, padding: '16px 20px', alignItems: 'center', borderBottom: '1px solid #f4f6f9' },
  badge: { display: 'inline-block', padding: '3px 8px', borderRadius: 6, fontSize: 12 },
  apiKey: { fontSize: 11, background: '#f4f6f9', padding: '4px 8px', borderRadius: 4, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 },
  keyBtn: { background: '#f4f6f9', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: '#062259' },
  actions: { display: 'flex', gap: 6 },
  editBtn: { background: '#EEF3FF', color: '#0F56E3', border: 'none', borderRadius: 6, width: 32, height: 32, cursor: 'pointer', fontSize: 14 },
  deleteBtn: { background: '#fff0ed', color: '#E46138', border: 'none', borderRadius: 6, width: 32, height: 32, cursor: 'pointer', fontSize: 14 },
  accessBtn: { background: '#f4f6f9', color: '#062259', border: 'none', borderRadius: 6, width: 32, height: 32, cursor: 'pointer', fontSize: 14 },
  accessRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f4f6f9', cursor: 'pointer' },
  // Le nom occupe la place restante, le compteur reste sur une seule ligne à
  // droite : sans ça un nom long ("Pôle Excellence opérationnelle & Nouveaux
  // produits") repousse et coupe le "0 outil(s)" sur deux lignes.
  rowName: { flex: 1, minWidth: 0, fontWeight: 600, fontSize: 14, lineHeight: 1.3 },
  rowMeta: { flexShrink: 0, whiteSpace: 'nowrap', fontSize: 12, color: '#7F7F7F' },
  checkbox: { flexShrink: 0, margin: 0 },
  badgeList: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  sectionLabel: { display: 'block', fontSize: 14, fontWeight: 600, color: '#062259', marginBottom: 2 },
  hint: { fontSize: 12, color: '#7F7F7F', marginBottom: 8 },
  checkList: { maxHeight: 220, overflowY: 'auto', border: '1.5px solid #D9D9D9', borderRadius: 8, padding: '4px 12px' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modal: { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #f4f6f9' },
  closeBtn: { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#7F7F7F' },
  modalBody: { padding: '24px', display: 'flex', flexDirection: 'column', gap: 16 },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '16px 24px', borderTop: '1px solid #f4f6f9' },
  field: { display: 'flex', flexDirection: 'column', gap: 6, flex: 1 },
  label: { fontSize: 13, fontWeight: 500, color: '#1a1a2e' },
  input: { padding: '10px 12px', borderRadius: 8, border: '1.5px solid #D9D9D9', fontSize: 14, color: '#1a1a2e', width: '100%' },
  row: { display: 'flex', gap: 16 },
  cancelBtn: { background: '#f4f6f9', color: '#7F7F7F', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, cursor: 'pointer' },
  saveBtn: { background: '#062259', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
};
